import { useMemo } from 'react';

import { Message as MessageType } from '~/features/ask-anything/types/chat';
import { intlService } from '~/i18n/IntlService';

import type { AgentMessage } from '../types';
import {
  extractWorkflowRecommendations,
  type PendingUserMessage,
  shouldDisplayMessage,
  transformAgentMessage,
  transformPendingMessage,
} from './utils';
import type { WorkflowSuggestion } from './WorkflowRoutingAgent';

interface UseWorkflowRoutingMessagesProps {
  messages: AgentMessage[];
  pendingUserMessages: PendingUserMessage[];
  streamMessage: AgentMessage | null;
  workflows: WorkflowSuggestion[] | null;
  thinkingEvents: string[];
  storedThinkingEvents: string[];
  isStreaming: boolean;
  isSendingMessage: boolean;
  fallbackMessage?: string;
}

interface WorkflowRoutingMessage extends MessageType {
  workflowChoices?: WorkflowSuggestion[];
  thinkingEvents?: string[];
  messageText?: string;
}

/**
 * Creates a completed workflow routing message
 */
const createCompletedWorkflowMessage = (
  agentMessage: AgentMessage,
): WorkflowRoutingMessage => {
  const workflowChoices = extractWorkflowRecommendations(agentMessage);

  return {
    id: agentMessage.id,
    text: intlService.getIntl().formatMessage({
      id: 'WorkflowRoutingAgent.suggestedWorkflows',
      defaultMessage: 'Workflow Routing',
      description: 'Header for suggested workflows section',
    }),
    userType: 'other' as const,
    timestamp: agentMessage.timestamp,
    choices: [],
    citations: agentMessage.citations ?? [],
    sources: agentMessage.sources ?? [],
    workflowChoices,
    thinkingEvents: [],
    messageText: agentMessage.text || undefined,
  };
};

/**
 * Creates a streaming workflow routing message
 */
const createStreamingWorkflowMessage = (
  streamMessage: AgentMessage,
  workflows: WorkflowSuggestion[] | null,
  thinkingEvents: string[],
  storedThinkingEvents: string[],
  isStreaming: boolean,
  isSendingMessage: boolean,
  fallbackMessage: string | undefined,
): WorkflowRoutingMessage => {
  const hasReceivedMessageEnd =
    streamMessage.text && streamMessage.text.trim() !== '';
  const shouldShowThinkingEvents =
    (isStreaming || isSendingMessage) && !hasReceivedMessageEnd;
  const finalThinkingEvents = shouldShowThinkingEvents
    ? storedThinkingEvents.length > 0
      ? storedThinkingEvents
      : thinkingEvents
    : [];

  const hasWorkflows = workflows && workflows.length > 0;
  const headerText = hasWorkflows
    ? intlService.getIntl().formatMessage({
        id: 'WorkflowRoutingAgent.suggestedWorkflows',
        defaultMessage: 'Workflow Routing',
        description: 'Header for suggested workflows section',
      })
    : intlService.getIntl().formatMessage({
        id: 'WorkflowRoutingAgent.usingWorkflowRouting',
        defaultMessage: 'Using Workflow Routing...',
        description: 'Header while analyzing with workflow routing',
      });

  return {
    id: streamMessage.id ?? 'workflow-routing-message',
    text: headerText,
    userType: 'other' as const,
    timestamp: streamMessage.timestamp || new Date(),
    choices: [],
    citations: [],
    sources: [],
    workflowChoices: workflows || [],
    thinkingEvents: finalThinkingEvents,
    messageText:
      fallbackMessage ||
      (hasReceivedMessageEnd ? streamMessage.text : undefined),
  };
};

export const useWorkflowRoutingMessages = ({
  messages,
  pendingUserMessages,
  streamMessage,
  workflows,
  thinkingEvents,
  storedThinkingEvents,
  isStreaming,
  isSendingMessage,
  fallbackMessage,
}: UseWorkflowRoutingMessagesProps) => {
  const chatMessages = useMemo(() => {
    const filtered = messages.filter((msg) =>
      shouldDisplayMessage(msg, messages, pendingUserMessages),
    );

    const messagesWithWorkflows = new Set(
      messages
        .filter((msg) => {
          const msgWorkflows = extractWorkflowRecommendations(msg);
          return msgWorkflows.length > 0 && msg.userType === 'other';
        })
        .map((msg) => msg.id),
    );

    return filtered
      .filter((msg) => {
        if (msg.userType !== 'other') return true;
        if (messagesWithWorkflows.has(msg.id)) return false;
        return true;
      })
      .map(transformAgentMessage);
  }, [messages, pendingUserMessages]);

  const messagesWithPending = useMemo(() => {
    const allMessages = [
      ...chatMessages,
      ...pendingUserMessages.map(transformPendingMessage),
    ];

    return allMessages.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }, [chatMessages, pendingUserMessages]);

  const completedWorkflowMessages = useMemo(() => {
    return messages
      .filter((msg) => {
        const workflowChoices = extractWorkflowRecommendations(msg);
        return workflowChoices.length > 0 && msg.userType === 'other';
      })
      .map((msg) => createCompletedWorkflowMessage(msg));
  }, [messages]);

  const streamingWorkflowMessage =
    useMemo<WorkflowRoutingMessage | null>(() => {
      const shouldShow =
        streamMessage ||
        isStreaming ||
        isSendingMessage ||
        pendingUserMessages.length > 0 ||
        fallbackMessage;

      if (!shouldShow) {
        return null;
      }

      if (streamMessage) {
        return createStreamingWorkflowMessage(
          streamMessage,
          workflows,
          thinkingEvents,
          storedThinkingEvents,
          isStreaming,
          isSendingMessage,
          fallbackMessage,
        );
      }

      if (fallbackMessage && !isStreaming && !isSendingMessage) {
        return {
          id: 'workflow-routing-message',
          text: intlService.getIntl().formatMessage({
            id: 'WorkflowRoutingAgent.suggestedWorkflows',
            defaultMessage: 'Workflow Routing',
            description: 'Header for suggested workflows section',
          }),
          userType: 'other' as const,
          timestamp: new Date(),
          choices: [],
          citations: [],
          sources: [],
          workflowChoices: [],
          thinkingEvents: [],
          messageText: fallbackMessage,
        };
      }

      if (isStreaming || isSendingMessage || pendingUserMessages.length > 0) {
        return {
          id: 'workflow-routing-message',
          text: intlService.getIntl().formatMessage({
            id: 'WorkflowRoutingAgent.usingWorkflowRouting',
            defaultMessage: 'Using Workflow Routing...',
            description: 'Header while analyzing with workflow routing',
          }),
          userType: 'other' as const,
          timestamp: new Date(),
          choices: [],
          citations: [],
          sources: [],
          workflowChoices: [],
          thinkingEvents:
            storedThinkingEvents.length > 0
              ? storedThinkingEvents
              : thinkingEvents,
          messageText: undefined,
        };
      }

      return null;
    }, [
      streamMessage,
      workflows,
      thinkingEvents,
      storedThinkingEvents,
      isStreaming,
      isSendingMessage,
      pendingUserMessages.length,
      fallbackMessage,
    ]);

  const allMessagesWithWorkflows = useMemo(() => {
    const result: MessageType[] = [...messagesWithPending];
    const messageIds = new Set(result.map((msg) => msg.id));

    completedWorkflowMessages.forEach((wfMsg) => {
      if (messageIds.has(wfMsg.id)) return;

      const wfTimestamp = new Date(wfMsg.timestamp).getTime();
      let insertIndex = result.length;

      for (let i = result.length - 1; i >= 0; i--) {
        const msg = result[i];
        const msgTimestamp = new Date(msg.timestamp).getTime();

        if (msg.userType === 'current' && msgTimestamp <= wfTimestamp) {
          insertIndex = i + 1;
          break;
        }
      }

      result.splice(insertIndex, 0, wfMsg);
      messageIds.add(wfMsg.id);
    });

    if (
      streamingWorkflowMessage &&
      !messageIds.has(streamingWorkflowMessage.id)
    ) {
      const lastUserMessageIndex = result.findLastIndex(
        (m) => m.userType === 'current',
      );
      const insertIndex =
        lastUserMessageIndex !== -1 ? lastUserMessageIndex + 1 : result.length;

      result.splice(insertIndex, 0, streamingWorkflowMessage);
    }

    return result;
  }, [
    messagesWithPending,
    completedWorkflowMessages,
    streamingWorkflowMessage,
  ]);

  return {
    allMessagesWithWorkflows,
    workflowRoutingMessage: streamingWorkflowMessage,
  };
};
